import useAuth from '@app/auth/use-auth';
import { ccfRoutes } from '@app/ccf/ccf-routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { OperationReportTypeEnum } from '@shared/api/types';
import GuideLayout from '@shared/components/guide-layout';
import { Form } from '@shared/components/ui/form';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom';
import { z } from 'zod';
import { importUploadSchema } from './utils';

export type ImportContext = {
  importUuid?: string;
  setImportUuid: (uuid: string) => void;
  numberOfActivities?: number;
  setNumberOfActivities: (num: number) => void;
  reportType: OperationReportTypeEnum;
};

export function useImport() {
  return useOutletContext<ImportContext>();
}

interface ImportLayoutProps {
  importType: 'food' | 'packaging' | 'mixed';
  onClosePath: string;
  reportType: OperationReportTypeEnum;
}

const backgroundColorMap: Record<ImportLayoutProps['importType'], string> = {
  food: 'from-teal-300  to-teal-100',
  packaging: 'from-warning-300  to-warning-100',
  mixed: 'from-primary-300  to-primary-100',
};

export default function ImportLayout({
  importType,
  onClosePath,
  reportType,
}: ImportLayoutProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [importUuid, setImportUuid] = useState<string | undefined>();
  const [numberOfActivities, setNumberOfActivities] = useState<
    number | undefined
  >();

  const form = useForm<z.infer<typeof importUploadSchema>>({
    resolver: zodResolver(importUploadSchema),
    defaultValues: { activityTypes: [] },
  });
  const context: ImportContext = {
    importUuid,
    setImportUuid,
    numberOfActivities,
    setNumberOfActivities,
    reportType,
  };

  useEffect(() => {
    if (!user?.organizationReportingStartingDate) {
      navigate(generatePath(ccfRoutes.REPORTING_PERIOD));
    }
  }, [navigate, user?.organizationReportingStartingDate]);

  return (
    <Form {...form}>
      <GuideLayout
        pageName={`${capitalizeFirstLetter(importType)} import`}
        onClosePath={onClosePath}
        context={context}
        className={backgroundColorMap[importType]}
      />
    </Form>
  );
}
